// Shared UI atoms for the Production Tracker pages — themed on Dream Machine's
// CSS variables so Punk (dark) + Unc (light) both render correctly.
import { useEffect, useRef, useState } from "react";

export const mono = "var(--font-mono)";
export const sans = "var(--font-sans)";

// status -> {bg, fg} using DM-safe colors
const TONE = {
  green: { bg: "rgba(16,163,12,.12)", fg: "#0ca30c" },
  amber: { bg: "rgba(217,119,6,.14)", fg: "#d97706" },
  red: { bg: "var(--rd)", fg: "var(--r)" },
  blue: { bg: "rgba(59,130,246,.12)", fg: "#3b82f6" },
  gray: { bg: "var(--s2)", fg: "var(--t3)" },
};
const VALUE_TONE = {
  "On Track": "green", Done: "green", Confirmed: "green", Delivered: "green", Resolved: "green", D: "green",
  "At Risk": "amber", Partial: "amber", "In Progress": "amber", Medium: "amber", Nearly: "amber", P: "amber",
  Delayed: "red", Blocked: "red", "Not Done": "red", High: "red", Open: "red", X: "red",
  // Cadence (Excel): A = Assigned/Working (present) = blue; L = Leave/Off = gray.
  A: "blue", L: "gray",
  Wrapped: "blue", Low: "gray",
};
export const toneOf = (v) => TONE[VALUE_TONE[v] || "gray"];

export function Chip({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const t = toneOf(value);
  const editable = options && onChange;
  useEffect(() => {
    if (!open) return;
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <span
        onClick={(e) => { e.stopPropagation(); if (editable) setOpen((o) => !o); }}
        style={{ display: "inline-flex", alignItems: "center", gap: 5, background: t.bg, color: t.fg,
          fontFamily: mono, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20,
          cursor: editable ? "pointer" : "default", whiteSpace: "nowrap", letterSpacing: ".02em" }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor", opacity: .8 }} />
        {value || "—"}{editable && <span style={{ fontSize: 8, opacity: .6 }}>▾</span>}
      </span>
      {open && (
        <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, zIndex: 50, background: "var(--s1)",
          border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", boxShadow: "0 8px 24px rgba(0,0,0,.25)", minWidth: 150, padding: 4 }}>
          {options.map((o) => (
            <div key={o} onClick={() => { setOpen(false); if (o !== value) onChange(o); }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", fontFamily: mono, fontSize: 11,
                color: o === value ? "var(--t1)" : "var(--t2)", fontWeight: o === value ? 700 : 400, cursor: "pointer", borderRadius: 4 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--s2)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: toneOf(o).fg }} />{o}
            </div>
          ))}
        </div>
      )}
    </span>
  );
}

export const inputStyle = {
  width: "100%", background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)",
  color: "var(--t1)", fontFamily: sans, fontSize: 13, padding: "8px 10px", boxSizing: "border-box",
};
export function Input(props) { return <input {...props} style={{ ...inputStyle, ...(props.style || {}) }} />; }
export function Textarea(props) { return <textarea {...props} style={{ ...inputStyle, ...(props.style || {}) }} />; }
export function Select({ options = [], value, onChange, allowEmpty, emptyLabel = "—", style }) {
  return (
    <select value={value} onChange={onChange} style={{ ...inputStyle, fontFamily: mono, fontSize: 12, ...(style || {}) }}>
      {allowEmpty && <option value="">{emptyLabel}</option>}
      {options.map((o) => typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
export function Btn({ children, onClick, kind = "primary", type = "button", disabled }) {
  const styles = {
    primary: { background: "var(--r)", color: "#fff", border: "none" },
    ghost: { background: "transparent", color: "var(--t2)", border: "1px solid var(--bdr)" },
    danger: { background: "transparent", color: "var(--r)", border: "none" },
  }[kind];
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...styles, fontFamily: mono, fontSize: 12, fontWeight: 600, letterSpacing: ".04em",
        padding: "8px 14px", borderRadius: "var(--radius-sm)", cursor: disabled ? "default" : "pointer",
        opacity: disabled ? .5 : 1, textTransform: "uppercase" }}>
      {children}
    </button>
  );
}
export function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <span style={{ display: "block", fontFamily: mono, fontSize: 10, fontWeight: 600, color: "var(--t3)",
        letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 5 }}>{label}</span>
      {children}
    </label>
  );
}
export function PageTitle({ title, sub, right }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
      <div>
        <h1 style={{ fontFamily: "var(--font-heading, var(--font-orb))", fontSize: 30, fontWeight: 700, color: "var(--t1)", margin: 0, textTransform: "uppercase" }}>{title}</h1>
        {sub && <p style={{ fontFamily: mono, fontSize: 12, color: "var(--t3)", letterSpacing: ".04em", marginTop: 4 }}>{sub}</p>}
      </div>
      {right}
    </div>
  );
}
export function Card({ children, style }) {
  return <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: "var(--radius-sm)", padding: 16, ...(style || {}) }}>{children}</div>;
}
export function Drawer({ title, onClose, children }) {
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.5)" }} />
      <div style={{ position: "absolute", top: 0, right: 0, height: "100%", width: "100%", maxWidth: 460,
        background: "var(--bg)", borderLeft: "1px solid var(--bdr)", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 3, background: "var(--r)" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid var(--bdr)" }}>
          <span style={{ fontFamily: mono, fontSize: 13, fontWeight: 700, color: "var(--t1)", letterSpacing: ".04em" }}>{title}</span>
          <span onClick={onClose} style={{ cursor: "pointer", color: "var(--t3)", fontSize: 18 }}>✕</span>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}
